/**
 * Delivery Calendar Widget
 * Main widget functionality for date/time selection
 */

class DeliveryCalendar {
  constructor() {
    this.container = document.getElementById('delivery-calendar_holder');
    if (!this.container) {
      console.warn('Delivery calendar - Container not found');
      return;
    }

    // Input elements
    this.postcodeInput = this.container.querySelector('.delivery-postcode-input');
    this.dateInput = this.container.querySelector('.delivery-date-input');
    this.timeInput = this.container.querySelector('.delivery-time-input');

    // Dropdowns
    this.dateDropdown = document.getElementById('delivery-date-dropdown');
    this.timeDropdown = document.getElementById('delivery-time-dropdown');

    // Loading indicators
    this.loadingHome = document.getElementById('delivery-loading-home');
    this.loadingCalendar = document.getElementById('delivery-loading-calendar');
    this.loadingTime = document.getElementById('delivery-loading-time');
    
    // Icon elements (to hide when loading)
    this.iconHome = document.querySelector('.delivery-icon-home');
    this.iconCalendar = document.querySelector('.delivery-icon-calendar');
    this.iconClock = document.querySelector('.delivery-icon-clock');

    // Calendar elements
    this.calendarGrid = this.dateDropdown?.querySelector('.delivery-calendar-grid');
    this.monthYear = this.dateDropdown?.querySelector('.delivery-month-year');
    this.prevMonthBtn = this.dateDropdown?.querySelector('.delivery-prev-month');
    this.nextMonthBtn = this.dateDropdown?.querySelector('.delivery-next-month');
    this.timeSlotsWrap = this.timeDropdown?.querySelector('.delivery-time-slots-wrap');

    // State
    this.currentPostcode = null;
    this.selectedDate = null;
    this.selectedTimeSlot = null;
    this.availableDates = [];
    this.availableTimeSlots = [];
    this.deliveryWindows = null; // Store the full delivery windows data from API
    this.currentMonth = new Date();
    this.debounceTimer = null;
    this.isOpeningDropdown = false; // Flag to prevent immediate closing

    // Initialize
    this.init();
  }

  init() {
    if (!this.container) return;

    // Load saved postcode from localStorage
    this.loadSavedPostcode();

    // Listen for postcode validation events from header
    window.addEventListener('postcodeValidated', (e) => {
      const postcode = e.detail?.postcode;
      if (postcode && this.postcodeInput) {
        this.postcodeInput.value = postcode;
        this.handlePostcodeChange(postcode);
      }
    });

    // Postcode input events
    if (this.postcodeInput) {
      this.postcodeInput.addEventListener('input', (e) => {
        const postcode = e.target.value.trim();
        this.debouncePostcodeValidation(postcode);
      });
      this.postcodeInput.addEventListener('focus', () => {
        // Only hide if not currently opening AND not clicking on date input
        // Check if date input is currently being interacted with
        const dateInputActive = document.activeElement === this.dateInput || 
                               this.dateInput === document.activeElement;
        
        if (!this.isOpeningDropdown && !dateInputActive) {
          console.log('Delivery calendar - Postcode input focused, hiding dropdowns');
          this.hideDateDropdown();
          this.hideTimeDropdown();
        } else {
          console.log('Delivery calendar - Postcode input focused, but NOT hiding (flag:', this.isOpeningDropdown, 'dateInputActive:', dateInputActive, ')');
        }
      });
    }

    // Date input events
    if (this.dateInput) {
      // Set flag on mousedown (before click) to prevent outside click handler from closing
      this.dateInput.addEventListener('mousedown', (e) => {
        if (!this.dateInput.disabled) {
          const isCurrentlyHidden = this.dateDropdown?.classList.contains('hidden');
          if (isCurrentlyHidden) {
            // Set flag IMMEDIATELY on mousedown (before any click handlers run)
            this.isOpeningDropdown = true;
            console.log('Delivery calendar - Flag set to true on mousedown');
          }
        }
      }, true); // Use capture phase
      
      this.dateInput.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        console.log('Delivery calendar - Date input clicked, disabled:', this.dateInput.disabled, 'flag:', this.isOpeningDropdown);
        if (!this.dateInput.disabled) {
          // Close time dropdown immediately if open - force close even if flag is set
          if (this.timeDropdown && !this.timeDropdown.classList.contains('hidden')) {
            console.log('Delivery calendar - Closing time dropdown before opening date dropdown');
            this._forceHideTime = true;
            this.isOpeningDropdown = false; // Clear flag to allow closing
            this.hideTimeDropdown();
          }
          
          // Check if dropdown is currently hidden
          const isCurrentlyHidden = this.dateDropdown?.classList.contains('hidden');
          if (isCurrentlyHidden) {
            // Ensure flag is set (should already be set from mousedown, but just in case)
            this.isOpeningDropdown = true;
            console.log('Delivery calendar - Flag set to true, opening dropdown');
            
            // Show dropdown immediately (synchronously) to prevent outside click handler from closing it
            // The flag is already set, so hideDateDropdown should respect it
            this.showDateDropdown();
          } else {
            // If already open, just close it
            this.isOpeningDropdown = false;
            this.hideDateDropdown();
          }
        } else {
          console.warn('Delivery calendar - Date input is disabled');
        }
      }, true); // Use capture phase
      
      this.dateInput.addEventListener('focus', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        console.log('Delivery calendar - Date input focused, disabled:', this.dateInput.disabled);
        if (!this.dateInput.disabled) {
          // Close time dropdown immediately if open - force close even if flag is set
          if (this.timeDropdown && !this.timeDropdown.classList.contains('hidden')) {
            console.log('Delivery calendar - Closing time dropdown before opening date dropdown (focus)');
            this._forceHideTime = true;
            this.isOpeningDropdown = false; // Clear flag to allow closing
            this.hideTimeDropdown();
          }
          
          // Only open if currently hidden
          const isCurrentlyHidden = this.dateDropdown?.classList.contains('hidden');
          if (isCurrentlyHidden) {
            // Set flag IMMEDIATELY and SYNCHRONOUSLY
            this.isOpeningDropdown = true;
            console.log('Delivery calendar - Flag set to true (focus), opening dropdown');
            // Show dropdown immediately
            this.showDateDropdown();
          }
        }
      }, true); // Use capture phase
    } else {
      console.warn('Delivery calendar - Date input element not found');
    }

    // Time input events
    if (this.timeInput) {
      this.timeInput.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        console.log('Delivery calendar - Time input clicked, disabled:', this.timeInput.disabled);
        if (!this.timeInput.disabled) {
          // Close date dropdown immediately if open - force close even if flag is set
          if (this.dateDropdown && !this.dateDropdown.classList.contains('hidden')) {
            console.log('Delivery calendar - Closing date dropdown before opening time dropdown');
            this._forceHide = true;
            this.isOpeningDropdown = false; // Clear flag to allow closing
            this.hideDateDropdown();
          }
          
          // Set flag to prevent immediate closing
          this.isOpeningDropdown = true;
          setTimeout(() => {
            this.toggleTimeDropdown();
            setTimeout(() => {
              this.isOpeningDropdown = false;
            }, 200);
          }, 10);
        } else {
          console.warn('Delivery calendar - Time input is disabled');
        }
      }, true); // Use capture phase
      
      this.timeInput.addEventListener('focus', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        console.log('Delivery calendar - Time input focused, disabled:', this.timeInput.disabled);
        if (!this.timeInput.disabled) {
          // Close date dropdown immediately if open - force close even if flag is set
          if (this.dateDropdown && !this.dateDropdown.classList.contains('hidden')) {
            console.log('Delivery calendar - Closing date dropdown before opening time dropdown (focus)');
            this._forceHide = true;
            this.isOpeningDropdown = false; // Clear flag to allow closing
            this.hideDateDropdown();
          }
          
          // Set flag to prevent immediate closing
          this.isOpeningDropdown = true;
          setTimeout(() => {
            this.showTimeDropdown();
            setTimeout(() => {
              this.isOpeningDropdown = false;
            }, 200);
          }, 10);
        }
      }, true); // Use capture phase
    } else {
      console.warn('Delivery calendar - Time input element not found');
    }

    // Calendar navigation
    if (this.prevMonthBtn) {
      this.prevMonthBtn.addEventListener('click', () => this.navigateMonth(-1));
    }
    if (this.nextMonthBtn) {
      this.nextMonthBtn.addEventListener('click', () => this.navigateMonth(1));
    }

    // Close dropdowns on outside click - use capture phase to run before other handlers
    let clickTimeout = null;
    document.addEventListener('click', (e) => {
      // FIRST: Check if clicking on date input or its related elements - return immediately
      const isDateInputClick = this.dateInput && (
        e.target === this.dateInput || 
        this.dateInput.contains(e.target) ||
        e.target.closest('.delivery-date-input') ||
        e.target.closest('.delivery-calendar-container') ||
        e.target.closest('.delivery-calendar-icon') ||
        e.target.closest('.delivery-input-wrap') ||
        e.target.closest('.delivery-widget-wrapper') // Also check the entire wrapper
      );
      
      if (isDateInputClick) {
        // Don't process this click for closing - the date input handler will manage the dropdown
        console.log('Delivery calendar - Click on date input area, ignoring outside click handler', {
          target: e.target,
          isDateInput: e.target === this.dateInput,
          contains: this.dateInput?.contains(e.target),
          closest: e.target.closest('.delivery-date-input') !== null
        });
        // Cancel any pending close
        clearTimeout(clickTimeout);
        return;
      }
      
      // SECOND: Check if we're currently opening a dropdown - check this early
      if (this.isOpeningDropdown) {
        console.log('Delivery calendar - Ignoring outside click, dropdown is opening (flag check)');
        clearTimeout(clickTimeout);
        return;
      }
      
      // SECOND: Check if clicking on date dropdown elements
      const isDateDropdownClick = this.dateDropdown && (
        e.target === this.dateDropdown || 
        this.dateDropdown.contains(e.target) ||
        e.target.closest('#delivery-date-dropdown') ||
        e.target.closest('.delivery-calendar-grid') ||
        e.target.closest('.delivery-widget-popper') ||
        e.target.closest('.delivery-prev-month') ||
        e.target.closest('.delivery-next-month') ||
        e.target.closest('.delivery-month-year')
      );
      
      // Check if click is on calendar day
      const isCalendarDayClick = e.target.closest('.delivery-calendar-day.available');
      if (isCalendarDayClick) {
        return;
      }
      
      // THIRD: Check if clicking on time input or dropdown
      const isTimeInputClick = this.timeInput && (
        e.target === this.timeInput || 
        this.timeInput.contains(e.target) ||
        e.target.closest('.delivery-time-input') ||
        e.target.closest('.delivery-time-container')
      );
      const isTimeDropdownClick = this.timeDropdown && (
        e.target === this.timeDropdown || 
        this.timeDropdown.contains(e.target) ||
        e.target.closest('#delivery-time-dropdown') ||
        e.target.closest('.delivery-time-slot') ||
        e.target.closest('.delivery-time-slots-wrap')
      );
      
      // FOURTH: Check if within entire container
      const isWithinContainer = this.container && this.container.contains(e.target);
      
      // Only close if click is truly outside - use a delay to avoid race conditions
      if (!isWithinContainer && 
          !isDateInputClick && !isDateDropdownClick && 
          !isTimeInputClick && !isTimeDropdownClick) {
        clearTimeout(clickTimeout);
        clickTimeout = setTimeout(() => {
          // Double-check flag before closing - also check if dropdowns are actually visible
          if (!this.isOpeningDropdown) {
            const dateDropdownVisible = this.dateDropdown && !this.dateDropdown.classList.contains('hidden');
            const timeDropdownVisible = this.timeDropdown && !this.timeDropdown.classList.contains('hidden');
            
            if (dateDropdownVisible || timeDropdownVisible) {
              console.log('Delivery calendar - Outside click timeout: closing dropdowns', {
                dateVisible: dateDropdownVisible,
                timeVisible: timeDropdownVisible
              });
              if (dateDropdownVisible) {
                this.hideDateDropdown();
              }
              if (timeDropdownVisible) {
                this.hideTimeDropdown();
              }
            }
          } else {
            console.log('Delivery calendar - Outside click timeout: NOT closing (flag is set)', {
              isOpeningDropdown: this.isOpeningDropdown
            });
          }
        }, 300); // Increased delay to avoid closing immediately after opening
      } else {
        // If click is on calendar elements, cancel any pending close
        clearTimeout(clickTimeout);
      }
    }, true); // Use capture phase to run before other click handlers

    // Close on ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hideDateDropdown();
        this.hideTimeDropdown();
      }
    });

    console.log('Delivery calendar - Initialized');
  }

  debouncePostcodeValidation(postcode) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      if (postcode) {
        this.handlePostcodeChange(postcode);
      }
    }, 500);
  }

  async handlePostcodeChange(postcode) {
    if (!postcode || postcode === this.currentPostcode) return;

    this.currentPostcode = postcode;
    this.resetDateAndTime();

    // Show loading
    this.setLoading('home', true);

    try {
      // Fetch delivery windows from API
      if (!window.deliveryProxy) {
        console.error('Delivery calendar - deliveryProxy not available');
        this.setLoading('home', false);
        return;
      }

      const response = await window.deliveryProxy.getDeliveryWindows(postcode);

      if (response.success && response.windows && response.windows.length > 0) {
        // Store the full delivery windows data for later use (no need to fetch again)
        this.deliveryWindows = response.windows;
        this.availableDates = window.deliveryProxy.extractDatesFromWindows(response.windows);
        this.enableDateInput();
        console.log('Delivery calendar - Available dates loaded:', this.availableDates);
        console.log('Delivery calendar - Available dates count:', this.availableDates.length);
        console.log('Delivery calendar - Delivery windows data stored for reuse');
        console.log('Delivery calendar - First few dates (raw):', this.availableDates.slice(0, 3).map(d => ({ date: d.date, dateType: typeof d.date, weekday: d.weekday })));
        console.log('Delivery calendar - Raw windows data (first item):', response.windows[0]);
        
        // CRITICAL: After loading delivery windows, validate any restored date from sessionStorage
        // This prevents stale dates from being used
        if (window.deliveryCheckout && window.deliveryCheckout.deliveryData) {
          const restoredDate = window.deliveryCheckout.deliveryData.date;
          if (restoredDate && !this.isDateAvailable(restoredDate)) {
            console.warn('Delivery calendar - Restored date from sessionStorage is not available:', restoredDate);
            console.warn('Delivery calendar - Clearing invalid restored date');
            // Clear invalid restored date
            if (this.selectedDate === restoredDate) {
              this.selectedDate = null;
              if (this.dateInput) {
                this.dateInput.value = '';
              }
              this.resetTime();
            }
            // Notify checkout to clear invalid data
            if (window.deliveryCheckout) {
              window.deliveryCheckout.deliveryData = null;
              sessionStorage.removeItem('delivery_data');
              window.deliveryCheckout.clearCartAttributes();
            }
          }
        }
        
        // If calendar is already open, re-render it with new dates
        if (this.dateDropdown && !this.dateDropdown.classList.contains('hidden')) {
          console.log('Delivery calendar - Re-rendering calendar with new dates');
          this.renderCalendar();
        }
        
        // After validating restored data, try to update cart attributes if there's valid restored data
        if (window.deliveryCheckout) {
          // This will now validate against current available dates before updating
          window.deliveryCheckout.updateCartAttributes();
        }
      } else {
        this.deliveryWindows = null; // Clear stored data if no windows available
        this.availableDates = [];
        this.disableDateInput();
        console.warn('Delivery calendar - No delivery windows available');
      }
    } catch (error) {
      console.error('Delivery calendar - Error fetching delivery windows:', error);
      this.deliveryWindows = null; // Clear stored data on error
      this.availableDates = [];
      this.disableDateInput();
    } finally {
      this.setLoading('home', false);
    }
  }

  async handleDateSelect(dateStr) {
    if (!dateStr || dateStr === this.selectedDate) return;

    // Close dropdown immediately when date is selected
    // Set force hide flag to bypass the opening check
    this._forceHide = true;
    this.isOpeningDropdown = false;
    this.hideDateDropdown();

    this.selectedDate = dateStr;
    this.dateInput.value = this.formatDate(dateStr);
    this.resetTime();

    // Use stored delivery windows data instead of making another API call
    if (!this.deliveryWindows || !window.deliveryProxy) {
      console.warn('Delivery calendar - No stored delivery windows data available, cannot extract time slots');
      this.availableTimeSlots = [];
      this.disableTimeInput();
      return;
    }

    // Extract time slots from the already-fetched delivery windows data
    this.availableTimeSlots = window.deliveryProxy.extractTimeSlotsForDate(this.deliveryWindows, dateStr);
    this.enableTimeInput();
    console.log('Delivery calendar - Time slots extracted from stored data for date', dateStr, ':', this.availableTimeSlots);
    console.log('Delivery calendar - Time slots count:', this.availableTimeSlots.length);
    console.log('Delivery calendar - Sample time slots:', this.availableTimeSlots.slice(0, 3).map(s => ({ time: s.time, display: s.display, available: s.available })));
    
    // Render time slots
    this.renderTimeSlots();
    
    // If time dropdown is already open, ensure it's updated
    if (this.timeDropdown && !this.timeDropdown.classList.contains('hidden')) {
      console.log('Delivery calendar - Time dropdown is open, ensuring it shows updated slots');
      this.renderTimeSlots();
    }
    
    // Update checkout integration (date selected, but no time yet - button should be disabled)
    this.updateCheckoutData();
  }

  handleTimeSlotSelect(timeSlot) {
    this.selectedTimeSlot = timeSlot;
    this.timeInput.value = timeSlot.display;
    this.hideTimeDropdown();

    // Update checkout integration
    this.updateCheckoutData();
  }

  loadSavedPostcode() {
    const savedPostcode = localStorage.getItem('validated_postcode');
    if (savedPostcode && this.postcodeInput) {
      this.postcodeInput.value = savedPostcode;
      this.handlePostcodeChange(savedPostcode);
    }
  }

  resetDateAndTime() {
    this.selectedDate = null;
    this.selectedTimeSlot = null;
    this.dateInput.value = '';
    this.timeInput.value = '';
    this.availableTimeSlots = [];
    // Note: We keep deliveryWindows until postcode changes, so dates remain available
    this.disableDateInput();
    this.disableTimeInput();
    
    // Update checkout integration (date/time cleared - button should be disabled)
    this.updateCheckoutData();
  }

  resetTime() {
    this.selectedTimeSlot = null;
    this.timeInput.value = '';
    this.disableTimeInput();
    
    // Update checkout integration (time cleared - button should be disabled if no time)
    this.updateCheckoutData();
  }

  enableDateInput() {
    if (this.dateInput) {
      this.dateInput.disabled = false;
      this.dateInput.classList.remove('disabled');
    }
  }

  disableDateInput() {
    if (this.dateInput) {
      this.dateInput.disabled = true;
      this.dateInput.classList.add('disabled');
    }
  }

  enableTimeInput() {
    if (this.timeInput) {
      this.timeInput.disabled = false;
      this.timeInput.classList.remove('disabled');
    }
  }

  disableTimeInput() {
    if (this.timeInput) {
      this.timeInput.disabled = true;
      this.timeInput.classList.add('disabled');
    }
  }

  setLoading(type, loading) {
    let loadingElement = null;
    let iconElement = null;
    
    switch (type) {
      case 'home':
        loadingElement = this.loadingHome;
        iconElement = this.iconHome;
        break;
      case 'calendar':
        loadingElement = this.loadingCalendar;
        iconElement = this.iconCalendar;
        break;
      case 'time':
        loadingElement = this.loadingTime;
        iconElement = this.iconClock;
        break;
    }

    if (loadingElement) {
      // Show/hide loading spinner
      if (loading) {
        // Remove hidden class and show the container
        loadingElement.classList.remove('hidden');
        loadingElement.style.display = 'flex';
        
        // Also find and show the spinner inside (it has 'hidden' class by default)
        const spinner = loadingElement.querySelector('.loading__spinner, .loading-spinner');
        if (spinner) {
          spinner.classList.remove('hidden');
          spinner.style.display = 'inline-block';
          console.log(`Delivery calendar - Spinner element found and shown for ${type}`);
        } else {
          console.warn(`Delivery calendar - Spinner element not found inside loading element for ${type}`);
        }
      } else {
        // Hide the container
        loadingElement.style.display = 'none';
        loadingElement.classList.add('hidden');
        
        // Also hide the spinner inside
        const spinner = loadingElement.querySelector('.loading__spinner, .loading-spinner');
        if (spinner) {
          spinner.classList.add('hidden');
          spinner.style.display = 'none';
        }
      }
    }
    
    if (iconElement) {
      // Hide/show icon when loading
      if (loading) {
        iconElement.style.display = 'none';
        iconElement.classList.add('hidden');
      } else {
        iconElement.style.display = 'block';
        iconElement.classList.remove('hidden');
      }
    }
    
    console.log(`Delivery calendar - setLoading(${type}, ${loading})`, {
      loadingElement: !!loadingElement,
      iconElement: !!iconElement,
      loadingDisplay: loadingElement?.style.display,
      iconDisplay: iconElement?.style.display
    });
  }

  toggleDateDropdown() {
    if (this.dateDropdown?.classList.contains('hidden')) {
      // Set flag before showing
      this.isOpeningDropdown = true;
      this.showDateDropdown();
    } else {
      // Clear flag before hiding
      this.isOpeningDropdown = false;
      this.hideDateDropdown();
    }
  }

  adjustContainerPadding() {
    // Find the cart__blocks container
    const cartBlocks = document.querySelector('.cart__blocks');
    if (!cartBlocks) return;
    
    // Check if any dropdown is open
    const dateOpen = this.dateDropdown && !this.dateDropdown.classList.contains('hidden');
    const timeOpen = this.timeDropdown && !this.timeDropdown.classList.contains('hidden');
    
    if (dateOpen || timeOpen) {
      // Calculate the height needed for the open dropdown
      const dateHeight = dateOpen ? this.dateDropdown.offsetHeight : 0;
      const timeHeight = timeOpen ? this.timeDropdown.offsetHeight : 0;
      const maxHeight = Math.max(dateHeight, timeHeight);
      
      // Add padding to accommodate the dropdown (add some extra for spacing)
      const paddingNeeded = maxHeight + 20; // 20px extra for spacing
      cartBlocks.style.paddingBottom = `${paddingNeeded}px`;
      console.log('Delivery calendar - Added padding to cart__blocks:', paddingNeeded, 'px');
    } else {
      // Remove padding when no dropdowns are open
      cartBlocks.style.paddingBottom = '';
      console.log('Delivery calendar - Removed padding from cart__blocks');
    }
  }

  showDateDropdown() {
    if (!this.dateDropdown) {
      console.warn('Delivery calendar - Date dropdown element not found');
      return;
    }
    
    // Don't show if already visible
    if (!this.dateDropdown.classList.contains('hidden')) {
      console.log('Delivery calendar - Date dropdown already visible');
      return;
    }
    
    // IMPORTANT: Close time dropdown FIRST, before setting any flags
    // This ensures the other dropdown is closed immediately
    if (this.timeDropdown && !this.timeDropdown.classList.contains('hidden')) {
      console.log('Delivery calendar - Closing time dropdown before opening date dropdown');
      this._forceHideTime = true;
      this.isOpeningDropdown = false; // Clear flag to allow closing
      this.hideTimeDropdown();
    }
    
    // Set flag FIRST, before doing anything else
    this.isOpeningDropdown = true;
    console.log('Delivery calendar - showDateDropdown: Flag set to true');
    
    // Now show the date dropdown
    this.dateDropdown.classList.remove('hidden');
    
    // Force display and ensure visibility
    this.dateDropdown.style.display = 'block';
    this.dateDropdown.style.visibility = 'visible';
    this.dateDropdown.style.opacity = '1';
    
    // Render calendar first
    this.renderCalendar();
    
    // Adjust container padding after rendering
    setTimeout(() => {
      this.adjustContainerPadding();
    }, 100);
    
    // Prevent the dropdown from being closed immediately by other handlers
    requestAnimationFrame(() => {
      // Double-check it's still supposed to be open
      if (!this.dateDropdown.classList.contains('hidden')) {
        this.dateDropdown.style.display = 'block';
      }
    });
    
    // Clear flag after dropdown is fully rendered - longer delay to prevent immediate closing
    setTimeout(() => {
      // Only clear flag if dropdown is still open
      if (!this.dateDropdown.classList.contains('hidden')) {
        this.isOpeningDropdown = false;
        console.log('Delivery calendar - Date dropdown fully opened, flag cleared after 800ms');
      } else {
        console.log('Delivery calendar - Date dropdown was closed before flag clear, keeping flag');
      }
    }, 800); // Increased delay to prevent immediate closing - give more time for all events to settle
    
    console.log('Delivery calendar - Date dropdown shown:', {
      hasHidden: this.dateDropdown.classList.contains('hidden'),
      display: this.dateDropdown.style.display,
      computedDisplay: window.getComputedStyle(this.dateDropdown).display,
      zIndex: window.getComputedStyle(this.dateDropdown).zIndex,
      isOpeningFlag: this.isOpeningDropdown
    });
  }

  hideDateDropdown() {
    if (!this.dateDropdown) return;
    
    // Don't hide if already hidden
    if (this.dateDropdown.classList.contains('hidden')) {
      return;
    }
    
    // Don't hide if we're currently opening (unless explicitly called from handleDateSelect)
    if (this.isOpeningDropdown && !this._forceHide) {
      const stack = new Error().stack;
      console.log('Delivery calendar - Ignoring hide, dropdown is opening', {
        isOpeningDropdown: this.isOpeningDropdown,
        forceHide: this._forceHide,
        hasHidden: this.dateDropdown.classList.contains('hidden'),
        caller: stack?.split('\n')[2]?.trim() || 'unknown',
        stack: stack?.split('\n').slice(0, 5).join('\n')
      });
      return;
    }
    
    // Additional check: if date input is focused or was just clicked, don't hide
    const dateInputFocused = this.dateInput && (
      document.activeElement === this.dateInput ||
      this.dateInput === document.activeElement ||
      this.dateInput.matches(':focus')
    );
    if (dateInputFocused && !this._forceHide) {
      console.log('Delivery calendar - Ignoring hide, date input is focused');
      return;
    }
    
    // Log where this is being called from for debugging
    const stack = new Error().stack;
    const caller = stack?.split('\n')[2]?.trim() || 'unknown';
    console.log('Delivery calendar - Hiding date dropdown', {
      isOpeningDropdown: this.isOpeningDropdown,
      forceHide: this._forceHide,
      caller: caller,
      fullStack: stack?.split('\n').slice(0, 8).join('\n')
    });
    
    // Clear the opening flag if we're explicitly hiding
    this.isOpeningDropdown = false;
    this._forceHide = false;
    this.dateDropdown.classList.add('hidden');
    this.dateDropdown.style.display = 'none';
    
    // Adjust container padding after hiding
    this.adjustContainerPadding();
  }

  toggleTimeDropdown() {
    if (this.timeDropdown?.classList.contains('hidden')) {
      this.showTimeDropdown();
    } else {
      this.hideTimeDropdown();
    }
  }

  showTimeDropdown() {
    if (!this.timeDropdown) {
      console.warn('Delivery calendar - Time dropdown element not found');
      return;
    }
    
    // IMPORTANT: Close date dropdown FIRST, before setting any flags
    // This ensures the other dropdown is closed immediately
    if (this.dateDropdown && !this.dateDropdown.classList.contains('hidden')) {
      console.log('Delivery calendar - Closing date dropdown before opening time dropdown');
      this._forceHide = true;
      this.isOpeningDropdown = false; // Clear flag to allow closing
      this.hideDateDropdown();
    }
    
    // Set flag to prevent immediate closing
    this.isOpeningDropdown = true;
    
    // Render time slots if not already rendered
    if (this.availableTimeSlots.length === 0) {
      console.log('Delivery calendar - No time slots available, rendering empty state');
      this.renderTimeSlots();
    } else {
      console.log('Delivery calendar - Rendering', this.availableTimeSlots.length, 'time slots');
      this.renderTimeSlots();
    }
    
    this.timeDropdown.classList.remove('hidden');
    
    // Force display and ensure visibility
    this.timeDropdown.style.display = 'block';
    this.timeDropdown.style.visibility = 'visible';
    this.timeDropdown.style.opacity = '1';
    
    // Adjust container padding after rendering
    setTimeout(() => {
      this.adjustContainerPadding();
    }, 100);
    
    // Clear flag after dropdown is fully rendered
    setTimeout(() => {
      this.isOpeningDropdown = false;
      console.log('Delivery calendar - Time dropdown fully opened, flag cleared');
    }, 300);
    
    console.log('Delivery calendar - Time dropdown shown:', {
      hasHidden: this.timeDropdown.classList.contains('hidden'),
      display: this.timeDropdown.style.display,
      computedDisplay: window.getComputedStyle(this.timeDropdown).display,
      timeSlotsCount: this.availableTimeSlots.length
    });
  }

  hideTimeDropdown() {
    if (!this.timeDropdown) return;
    
    // Don't hide if already hidden
    if (this.timeDropdown.classList.contains('hidden')) {
      return;
    }
    
    // Don't hide if we're currently opening (unless explicitly forced)
    if (this.isOpeningDropdown && !this._forceHideTime) {
      console.log('Delivery calendar - Ignoring time dropdown hide, dropdown is opening');
      return;
    }
    
    // Clear flags
    this._forceHideTime = false;
    console.log('Delivery calendar - Hiding time dropdown');
    this.timeDropdown.classList.add('hidden');
    this.timeDropdown.style.display = 'none';
    
    // Adjust container padding after hiding
    this.adjustContainerPadding();
  }

  renderCalendar() {
    if (!this.calendarGrid || !this.monthYear) return;

    console.log('Delivery calendar - Rendering calendar, available dates:', this.availableDates.length);

    // Update month/year display
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    this.monthYear.textContent = `${monthNames[this.currentMonth.getMonth()]} ${this.currentMonth.getFullYear()}`;

    // Clear grid
    this.calendarGrid.innerHTML = '';

    // Get first day of month and number of days
    const firstDay = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1);
    const lastDay = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    // Add day headers
    const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    dayHeaders.forEach(day => {
      const header = document.createElement('div');
      header.className = 'delivery-calendar-day-header';
      header.textContent = day;
      this.calendarGrid.appendChild(header);
    });

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      const empty = document.createElement('div');
      empty.className = 'delivery-calendar-day empty';
      this.calendarGrid.appendChild(empty);
    }

    // Track available dates found in this month
    let availableCount = 0;

    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), day);
      const dateStr = this.formatDateString(date);
      const dayElement = document.createElement('div');
      dayElement.className = 'delivery-calendar-day';

      // Check if date is available
      const isAvailable = this.isDateAvailable(dateStr);
      const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
      const isSelected = dateStr === this.selectedDate;

      if (isPast) {
        dayElement.classList.add('past');
      } else if (isAvailable) {
        dayElement.classList.add('available');
        availableCount++;
        if (isSelected) {
          dayElement.classList.add('selected');
        }
        dayElement.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          console.log('Delivery calendar - Date clicked:', dateStr);
          // Clear flag and close dropdown immediately
          this.isOpeningDropdown = false;
          this.handleDateSelect(dateStr);
        }, true); // Use capture phase to run before outside click handler
      } else {
        dayElement.classList.add('unavailable');
      }

      dayElement.textContent = day;
      this.calendarGrid.appendChild(dayElement);
    }

    console.log('Delivery calendar - Calendar rendered, available dates in month:', availableCount);
    if (availableCount === 0 && this.availableDates.length > 0) {
      console.warn('Delivery calendar - No available dates found in current month, but we have', this.availableDates.length, 'total dates');
      console.log('Delivery calendar - Current month:', this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1);
      console.log('Delivery calendar - Sample available dates (raw):', this.availableDates.slice(0, 5).map(d => d.date));
      
      // Show what dates we're checking in the current month
      const firstDayOfMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1);
      const lastDayOfMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 0);
      const firstDateStr = this.formatDateString(firstDayOfMonth);
      const lastDateStr = this.formatDateString(lastDayOfMonth);
      console.log('Delivery calendar - Checking dates from', firstDateStr, 'to', lastDateStr);
      
      // Show a sample comparison
      if (this.availableDates.length > 0) {
        const sampleAvailable = this.availableDates[0].date;
        const sampleCheck = this.formatDateString(new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 15));
        console.log('Delivery calendar - Date format comparison - Available:', sampleAvailable, 'Type:', typeof sampleAvailable, 'Check:', sampleCheck, 'Match:', sampleAvailable === sampleCheck);
      }
    }
  }

  isDateAvailable(dateStr) {
    if (!this.availableDates || this.availableDates.length === 0) {
      return false;
    }
    
    // Normalize the date string we're checking (should already be YYYY-MM-DD)
    const normalizedCheckDate = dateStr.trim();
    
    // Check exact match
    const isAvailable = this.availableDates.some(d => {
      if (!d || !d.date) return false;
      const normalizedAvailableDate = String(d.date).trim();
      return normalizedAvailableDate === normalizedCheckDate;
    });
    
    return isAvailable;
  }

  renderTimeSlots() {
    if (!this.timeSlotsWrap) {
      console.warn('Delivery calendar - Time slots wrap element not found');
      return;
    }

    console.log('Delivery calendar - Rendering time slots, count:', this.availableTimeSlots.length);

    this.timeSlotsWrap.innerHTML = '';

    if (this.availableTimeSlots.length === 0) {
      const noSlots = document.createElement('div');
      noSlots.className = 'delivery-time-slot-empty';
      noSlots.textContent = 'No time slots available for this date';
      this.timeSlotsWrap.appendChild(noSlots);
      console.log('Delivery calendar - No time slots to render, showing empty message');
      return;
    }

    this.availableTimeSlots.forEach((slot, index) => {
      const slotElement = document.createElement('button');
      slotElement.type = 'button';
      slotElement.className = 'delivery-time-slot';
      if (this.selectedTimeSlot && this.selectedTimeSlot.id === slot.id) {
        slotElement.classList.add('selected');
      }

      const timeText = document.createElement('span');
      timeText.className = 'delivery-time-slot__time';
      timeText.textContent = slot.display || slot.time || 'Time slot';

      const capacityText = document.createElement('span');
      capacityText.className = 'delivery-time-slot__capacity';
      const capacity = slot.availableCapacity !== undefined ? slot.availableCapacity : (slot.capacity - (slot.currentOrders || 0));
      capacityText.textContent = `${capacity} available`;

      slotElement.appendChild(timeText);
      slotElement.appendChild(capacityText);

      slotElement.addEventListener('click', () => {
        console.log('Delivery calendar - Time slot selected:', slot);
        this.handleTimeSlotSelect(slot);
      });
      
      this.timeSlotsWrap.appendChild(slotElement);
      
      if (index < 3) {
        console.log('Delivery calendar - Rendered time slot:', slot.display, 'Available capacity:', capacity);
      }
    });
    
    console.log('Delivery calendar - Time slots rendered, total:', this.availableTimeSlots.length);
  }

  navigateMonth(direction) {
    this.currentMonth.setMonth(this.currentMonth.getMonth() + direction);
    this.renderCalendar();
  }

  formatDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }

  formatDateString(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  updateCheckoutData() {
    // This will be handled by delivery-checkout.js
    // Dispatch event for checkout integration
    window.dispatchEvent(new CustomEvent('deliveryDataUpdated', {
      detail: {
        postcode: this.currentPostcode,
        date: this.selectedDate,
        timeSlot: this.selectedTimeSlot
      }
    }));
  }

  getDeliveryData() {
    return {
      postcode: this.currentPostcode,
      date: this.selectedDate,
      timeSlot: this.selectedTimeSlot
    };
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.deliveryCalendar = new DeliveryCalendar();
});

// Make class globally available
window.DeliveryCalendar = DeliveryCalendar;
